import { Box, Button, styled, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useNavigate } from "react-router-dom";
import {
  EPOCUS_TEAL_DARK,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { ReviewerQueueRow } from "../../_lib/reviewer-queue-row";
import {
  BigValue,
  Card,
  MetaLabel,
  SummaryGrid,
} from "../../_lib/stat-primitives";
import { useReviewerQueue } from "../../_lib/use-reviewer-queue";
import { useReviewerTurnaroundStats } from "../../_lib/use-reviewer-turnaround-stats";
import { linkToEpocusReview } from "../../links";

const NEXT_IN_QUEUE_LIMIT = 4;

const SectionRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CountChip = styled(Box)({
  padding: "1px 8px",
  borderRadius: 20,
  fontSize: rem(12),
  fontWeight: 700,
  backgroundColor: "rgba(46,156,166,0.12)",
  color: EPOCUS_TEAL_DARK,
});

const Note = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const Subtitle = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 4,
});

const Stat: FC<{
  label: string;
  value: string;
  subtitle: string;
}> = ({ label, value, subtitle }) => (
  <Card>
    <MetaLabel>{label}</MetaLabel>
    <BigValue>{value}</BigValue>
    <Subtitle>{subtitle}</Subtitle>
  </Card>
);

export const ReviewerHomeSections: FC = () => {
  const navigate = useNavigate();
  const { entries, pendingCount, poolCount } = useReviewerQueue();
  const { reviewedToday, sourcesToday } = useReviewerTurnaroundStats();

  const assignedCount = pendingCount - poolCount;

  const next = entries.slice(0, NEXT_IN_QUEUE_LIMIT);

  return (
    <>
      <SummaryGrid>
        <Stat
          label="Pending your review"
          value={String(pendingCount)}
          subtitle={`${assignedCount} assigned to you · ${poolCount} in the pool`}
        />
        <Stat
          label="Reviewed today"
          value={String(reviewedToday)}
          subtitle={`across ${sourcesToday} ${sourcesToday === 1 ? "source" : "sources"}`}
        />
      </SummaryGrid>

      <Box>
        <SectionRow>
          <SectionTitle>Next in your queue</SectionTitle>
          <CountChip>{pendingCount}</CountChip>
          <Note>oldest first</Note>
        </SectionRow>

        {next.length === 0 ? (
          <Card>
            <Note>Nothing is waiting on you right now.</Note>
          </Card>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
            {next.map((entry) => (
              <ReviewerQueueRow key={entry.encounter.id} entry={entry} />
            ))}
          </Box>
        )}

        <Box style={{ marginTop: 10 }}>
          <Button
            onClick={() => navigate(linkToEpocusReview())}
            endIcon={<ChevronRightIcon />}
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            View full queue
          </Button>
        </Box>
      </Box>
    </>
  );
};
